import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { getTgUserId } from "@/helpers/tgWebApp"
import i18n from "@/localization"
import { STORE_CURRENCY } from "@/store/apis/storePaymentsAPI"
import { readState as readDevToolsState } from "@/store/slices/devTools"
import { type IAppSliceState, PAGE_STYLE } from "./types"

const rawIsTMA = !!getTgUserId() || import.meta.env.VITE_NODE_ENV === "local"
const { tmaOverride } = readDevToolsState()
const resolvedIsTMA = tmaOverride === "auto" ? rawIsTMA : tmaOverride === "tma"

export const initialState: IAppSliceState = {
  errorObject: {
    message: "",
    serverMessage: "",
    status: 0,
    code: 0,
    data: null,
    url: ""
  },
  language: i18n.language || "en",
  isLoaded: false,
  isLabrUser: true,
  pageStyle: PAGE_STYLE.BROWN_LIGHT,
  isDevMode: import.meta.env.VITE_IS_DEV === "true",
  isDesktop: true,
  isPhoneDevice: false,
  maintenance: {
    isUnderMaintenance: false
  },
  volume: 0,
  isTMA: resolvedIsTMA,
  reAuthState: "idle",
  screen: {
    width: 0,
    height: 0
  },
  storeCurrency: STORE_CURRENCY.EXTERNAL_TON,
  isDev: ["local", "development"].includes(import.meta.env.VITE_NODE_ENV)
}

export const AppSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setIsLabrUser: (state, { payload }: PayloadAction<boolean>) => {
      state.isLabrUser = payload
    },
    setLanguage: (
      state,
      { payload }: PayloadAction<IAppSliceState["language"]>
    ) => {
      state.language = payload
    },
    setIsLoaded: (
      state,
      { payload }: PayloadAction<IAppSliceState["isLoaded"]>
    ) => {
      state.isLoaded = payload
    },
    setPageStyle: (
      state,
      { payload }: PayloadAction<IAppSliceState["pageStyle"]>
    ) => {
      state.pageStyle = payload
    },
    setIsDesktop: (
      state,
      { payload }: PayloadAction<IAppSliceState["isDesktop"]>
    ) => {
      state.isDesktop = payload
    },
    setIsPhoneDevice: (
      state,
      { payload }: PayloadAction<IAppSliceState["isPhoneDevice"]>
    ) => {
      state.isPhoneDevice = payload
    },
    setMaintenance: (
      state,
      { payload }: PayloadAction<IAppSliceState["maintenance"]>
    ) => {
      state.maintenance = payload
    },
    setVolume: (
      state,
      { payload }: PayloadAction<IAppSliceState["volume"]>
    ) => {
      state.volume = payload
    },
    setScreen: (
      state,
      { payload }: PayloadAction<IAppSliceState["screen"]>
    ) => {
      state.screen = payload
    },
    setReAuthState: (
      state,
      { payload }: PayloadAction<IAppSliceState["reAuthState"]>
    ) => {
      state.reAuthState = payload
    },
    setStoreCurrency: (
      state,
      { payload }: PayloadAction<IAppSliceState["storeCurrency"]>
    ) => {
      state.storeCurrency = payload
    }
  }
})

export const {
  setLanguage,
  setIsLabrUser,
  setIsLoaded,
  setPageStyle,
  setIsDesktop,
  setIsPhoneDevice,
  setMaintenance,
  setVolume,
  setScreen,
  setReAuthState,
  setStoreCurrency
} = AppSlice.actions
